import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { useAlreadyExistingReportedData } from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/hooks/alreadyExistingReportedData'
import ReportedDataFoundStep from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/ReportedDataFoundStep'
import SelectTemplateForScraping from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/SelectTemplateForScraping'
import { insertIf } from '@app/src/utils/helpersTs'
import { Dialog, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useScheduleScreening } from './hooks/scheduleScreening'
import ScheduleProviderScreeningSummary from './ScheduleProviderScreeningSummary'
import ScheduleScreeningSuccessView from './ScheduleScreeningSuccessView'

export enum SCHEDULE_SCREENING_QUESTION_KEYS {
  PROVIDER_IDS = 'providerIds',
  TEMPLATES = 'templates',
}

type ScheduleProviderScreeningModalProps = {
  open: boolean
  onClose: () => void
  preselectedTemplateId?: number
}

export type ScheduleScreeningFormData = {
  providerIds: number[]
  templates: Array<{ id: number; title: string }>
}

const WIDTH = 584

const ScheduleProviderScreeningModal: React.FC<ScheduleProviderScreeningModalProps> = ({
  open,
  onClose,
  preselectedTemplateId,
}) => {
  const { formatMessage } = useIntl()
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { providersToConnect } = useProvidersModal()
  const { scheduleScreening, isScheduling } = useScheduleScreening()

  const hasPreselectedTemplate = Boolean(preselectedTemplateId)

  const formMethods = useForm<ScheduleScreeningFormData>({
    shouldUnregister: false,
    defaultValues: {
      [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS]: providersToConnect.map(provider => provider.id),
      [SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES]: [],
    },
  })
  const { handleSubmit, reset, watch, setValue } = formMethods

  const { settings } = useOrganizationSettings()

  const remainingProviderScreenings = settings?.remainingProviderScreenings ?? 0

  const watchProviders = watch(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS)
  const watchTemplates = watch(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES)
  const templateIds = watchTemplates?.map(q => q.id) ?? []
  const totalScreeningsToPerform = templateIds.length * watchProviders.length
  const remainingScreeningsEnough = remainingProviderScreenings - totalScreeningsToPerform > -1

  const { alreadyExistingReportedData, alreadyExistingReportedDataLoading, alreadyExistingReportedDataFetched } =
    useAlreadyExistingReportedData({ enabled: activeStep > 0, templateIds })

  useEffect(() => {
    if (providersToConnect?.length) {
      formMethods.setValue(
        SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS,
        providersToConnect.map(({ id }) => id),
        { shouldDirty: true },
      )
    }
  }, [providersToConnect, formMethods.setValue])

  useEffect(() => {
    if (open && preselectedTemplateId) {
      setValue(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES, [
        { id: preselectedTemplateId, title: 'Baseline Assessment' },
      ])
    }
  }, [open, preselectedTemplateId, setValue])

  const [showSuccessScreen, setShowSuccessScreen] = useState(false)

  const QUESTIONS = [
    ...insertIf(
      !hasPreselectedTemplate,
      <CreationModalStep
        key={SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES}
        title={formatMessage({ id: 'form.scheduleProviderScreening.selectQuestionnaire.title' })}
        fieldnames={[SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES]}
        disableInitialBack
        overrideButtonTextId="general.continue"
        widthOverride={WIDTH}
      >
        <SelectTemplateForScraping name={SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES} />
      </CreationModalStep>,
    ),
    ...insertIf(
      !alreadyExistingReportedDataFetched || alreadyExistingReportedData.length > 0,
      <ReportedDataFoundStep
        key="reportedDataFound"
        widthOverride={WIDTH}
        alreadyExistingReportedData={alreadyExistingReportedData}
        alreadyExistingReportedDataLoading={alreadyExistingReportedDataLoading}
      />,
    ),
    <CreationModalStep
      key={SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS}
      title={formatMessage({ id: 'form.scheduleProviderScreening.summary.title' })}
      description={formatMessage({ id: 'form.scheduleProviderScreening.summary.description' })}
      fieldnames={
        hasPreselectedTemplate
          ? [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS]
          : [SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS, SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES]
      }
      hideNextButton
      disableInitialBack
      overrideButtonTextId="form.scheduleProviderScreening.summary.confirm"
      isLoading={isScheduling}
      nextButtonProps={{ disabled: !remainingScreeningsEnough }}
      widthOverride={WIDTH}
    >
      <ScheduleProviderScreeningSummary
        remainingProviderScreenings={remainingProviderScreenings}
        totalScreeningsToPerform={totalScreeningsToPerform}
        remainingScreeningsEnough={remainingScreeningsEnough}
      />
    </CreationModalStep>,
  ]

  const activeQuestion = useMemo(() => QUESTIONS[activeStep - 1], [activeStep, QUESTIONS])

  const onSubmit = async (values: ScheduleScreeningFormData) => {
    await scheduleScreening({
      providerIds: values.providerIds,
      templateIds: values.templates.map(template => template.id),
    })

    setShowSuccessScreen(true)
  }

  useEffect(() => {
    return () => {
      setActiveStep(1)
      reset()
      setShowSuccessScreen(false)
    }
  }, [open])

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
  }, [QUESTIONS.length])

  return (
    <Dialog open={open} onClose={onClose} fullScreen closeAfterTransition>
      {showSuccessScreen ? (
        <ScheduleScreeningSuccessView providerIds={watchProviders} onClose={onClose} />
      ) : (
        <CreationModalContainer title={formatMessage({ id: 'general.aiScreening' })} onClose={onClose}>
          <FormProvider {...formMethods}>
            <Stack component="form" flexGrow={1} alignItems="center" onSubmit={handleSubmit(onSubmit)}>
              {activeQuestion}
            </Stack>
          </FormProvider>
        </CreationModalContainer>
      )}
    </Dialog>
  )
}

export default ScheduleProviderScreeningModal
